import {
  ReactElement,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import {
  IonIcon,
  IonToggle,
  useIonRouter,
  useIonViewWillEnter,
} from "@ionic/react";
import {
  addCircleOutline,
  bugOutline,
  chatbubbleEllipsesOutline,
  chevronDownOutline,
  chevronUpOutline,
  createOutline,
  informationCircleOutline,
  languageOutline,
  mailOutline,
  logOutOutline,
  notificationsOutline,
  schoolOutline,
  settingsOutline,
} from "ionicons/icons";
import styled from "styled-components";
import { useTranslation } from "react-i18next";

import ResonanceLayout from "../../components/organisms/ResonanceContext/ResonanceLayout";
import { useResonanceLayout } from "../../components/organisms/ResonanceContext/ResonanceLayoutContext";
import AuthContext from "../../contexts/AuthContext";
import { useConfig } from "../../contexts/ConfigContext";
import { isPasswordSignInMethod, ProfileFieldKeyType } from "../../types/profile";
import { isRoleAccessFieldEnabled } from "../../helper/roleAccess";
import ChangeEmailModal from "../Profile/ChangeEmailModal";
import ChangeNicknameModal from "../Profile/ChangeNicknameModal";
import ChangePhoneNumberModal from "../Profile/ChangePhoneNumberModal";
import ChangePwdModal from "../Profile/ChangePasswordModal";
import LeaveStudyModal from "../Profile/LeaveStudyModal";
import NirOppositionModal from "../Profile/NirOppositionModal";
import LegalMentionModal from "../Profile/LegalMentionModal";
import ReportBugModal from "../Profile/ReportBugModal";
import AboutModal from "../Profile/AboutModal";
import LanguageSelectorModal from "../../components/molecules/LanguageSelector/LanguageSelectorModal/LanguageSelectorModal";
import { getNirStatus, NirStatusResponse, patchNotificationPreferences } from "../../services/user.service";
import { getStudyLangs } from "../../services/study.service";
import { ensurePushNotificationsEnabled } from "../../helper/notifications";
import { ENV } from "../../environments/environment";
import { isSingleStudy } from "../../helper/study";
import { resetCaches } from "../../services/questionnaire.service";

type SectionId = "account" | "notifications" | "about";
type NotificationChannel = "email" | "sms" | "push";

const DisplayAccount = (): ReactElement => {
  const { t } = useTranslation();
  const { user, getCurrent, endSession } = useContext(AuthContext);
  const { study, selectedRole, app } = useConfig();
  const ionRouter = useIonRouter();
  const { setResonanceLayout } = useResonanceLayout();

  const [isLeaveStudyModalOpen, setIsLeaveStudyModalOpen] = useState(false);
  const [isChangeEmailModalOpen, setIsChangeEmailModalOpen] = useState(false);
  const [isChangePhoneNumberModalOpen, setIsChangePhoneNumberModalOpen] =
    useState(false);
  const [isChangeNicknameModalOpen, setIsChangeNicknameModalOpen] =
    useState(false);
  const [isChangePwdModalOpen, setIsChangePwdModalOpen] = useState(false);
  const [isLegalMentionOpen, setIsLegalMentionOpen] = useState(false);
  const [isReportBugOpen, setIsReportBugOpen] = useState(false);
  const [isAboutOpen, setIsAboutOpen] = useState(false);
  const [langModalVisible, setLangModalVisible] = useState(false);

  // All sections start collapsed (user call 2026-07-03).
  const [expanded, setExpanded] = useState<Record<SectionId, boolean>>({
    account: false,
    notifications: false,
    about: false,
  });
  const [notificationLoading, setNotificationLoading] = useState<Record<NotificationChannel, boolean>>({
    email: false,
    sms: false,
    push: false,
  });
  const [pushPermissionError, setPushPermissionError] = useState(false);

  // SNDS-linkage opposition (NIR studies only). Harmless probe on non-NIR
  // deployments/studies: the API answers {exists:false} and the rows never
  // render. Booleans only — no value ever reaches this page.
  const [nirStatus, setNirStatus] = useState<NirStatusResponse | null>(null);
  const [isNirOppositionModalOpen, setIsNirOppositionModalOpen] = useState(false);
  useEffect(() => {
    if (!study?.id) return;
    getNirStatus(study.id)
      .then(({ data }) => setNirStatus(data))
      .catch(() => setNirStatus(null));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [study?.id]);

  // A single-language study has nothing to choose: the selector would list one
  // already-checked row. Gate on the STUDY's languages rather than per project,
  // so the row returns on its own the day a researcher adds a second one.
  // Starts hidden so it can't flash in and out on a monolingual study.
  const [hasLangChoice, setHasLangChoice] = useState(false);
  useEffect(() => {
    if (!study?.id) return;
    let cancelled = false;
    getStudyLangs(study.id)
      .then(({ data }) => {
        if (!cancelled) setHasLangChoice((data?.additional_langs?.length ?? 0) > 0);
      })
      .catch(() => {
        /* fail-soft: the row simply stays hidden */
      });
    return () => {
      cancelled = true;
    };
  }, [study?.id]);

  useIonViewWillEnter(() => {
    // The new read-only identity rows read user.attributes, which an identity
    // question can rewrite mid-study (firstname/lastname/address/external_id are
    // written straight onto the users row). Without this the page would keep
    // showing whatever was fetched at login until a notification toggle happened
    // to call getCurrent(). Fail-soft: on error the previous values just stay.
    getCurrent().catch(() => {
      /* keep the values already in context */
    });

    setResonanceLayout({
      disableHeader: false,
      disableNav: false,
      backgroundColor: "white",
      welcomePage: false,
      title: "mentalplus_account_page_title",
    });
  });

  const getValidEmail = (email?: string | null) => {
    if (!email) return null;
    return email;
  };

  const getValidPhone = (phone?: string | null) => {
    if (!phone) return null;
    if (phone === "0607080910") return null;
    return phone;
  };

  const getValidNickname = (nickname?: string | null) => {
    if (!nickname) return null;
    if (!nickname.trim()) return null;
    return nickname;
  };

  /**
   * Two INDEPENDENT axes decide what this section shows — keep them apart:
   *
   *   collectsField(key)  "does the study collect this field for my role?"
   *                       (researcher's access-fields table in Diapason) -> is
   *                       there a ROW at all
   *   canChangePassword   "does my role sign in with a password?" -> does that
   *                       row get an EDIT affordance
   *
   * A role that signs in by password can still not collect a pseudonym, and a
   * role that collects an email can still be NO_SIGNIN — conflating the two is
   * what made the page offer fields the study never asked for.
   */
  const roleAccess = selectedRole?.access;

  const collectsField = useCallback(
    (key: ProfileFieldKeyType): boolean =>
      // Fail CLOSED, deliberately, on both halves:
      //  - isRoleAccessFieldEnabled dereferences access.fields, and selectedRole is
      //    undefined in ConfigContext's initial state and while /welcome resolves;
      //  - it also answers true for a key that is ABSENT from fields (undefined
      //    !== 'HIDDEN'). A live API always emits all 8 keys, but a stale cached
      //    welcome payload need not — and "config unknown" must not mean "show
      //    every identity field".
      Boolean(roleAccess?.fields?.length) && isRoleAccessFieldEnabled(roleAccess, key),
    [roleAccess]
  );

  const canChangePassword = useMemo(
    () => isPasswordSignInMethod(selectedRole?.access?.signInMode),
    [selectedRole]
  );

  const canChangeNickname = canChangePassword;

  const nickname = useMemo(
    () => getValidNickname(user?.attributes?.nickname),
    [user]
  );

  const validEmail = getValidEmail(user?.attributes?.email);
  const pendingEmail = getValidEmail(user?.pending_email);
  const validPhone = getValidPhone(user?.attributes?.phone_number);
  const pendingPhone = getValidPhone(user?.pending_phone_number);

  const displayEmail = pendingEmail || validEmail;
  const displayPhone = pendingPhone || validPhone;
  const hasEmailValue = Boolean(displayEmail);
  const hasPhoneValue = Boolean(displayPhone);
  const canEnableEmailNotifications = Boolean(displayEmail);
  const canEnableSmsNotifications = Boolean(displayPhone);

  const notificationPreferences = user?.notification_preferences;
  const emailNotificationsEnabled = notificationPreferences?.email ?? true;
  const smsNotificationsEnabled = notificationPreferences?.sms ?? true;
  const pushNotificationsEnabled = notificationPreferences?.push ?? true;
  const effectivePushNotificationsEnabled =
    user?.notification_effective_status?.push ?? pushNotificationsEnabled;

  const toggleSection = (id: SectionId) => {
    setExpanded((prev) => ({
      ...prev,
      [id]: !prev[id],
    }));
  };

  const updateNotificationPreference = async (
    channel: NotificationChannel,
    value: boolean
  ) => {
    if (!study?.id || notificationLoading[channel]) return;

    setNotificationLoading((prev) => ({ ...prev, [channel]: true }));

    try {
      if (channel === "push" && value) {
        let publicKey = ENV.VAPID_PUBLIC_KEY;

        // Multi-study only: resolve the per-study key file baked into
        // /assets/keys. Single-study uses the ENV key directly.
        if (!isSingleStudy()) {
          try {
            const response = await fetch(`/assets/keys/${study.id}-web-key.json`);
            if (response.ok) {
              const data = await response.json();
              publicKey = data?.public_key || publicKey;
            }
          } catch (_error) {
            // Keep ENV key fallback when study key file is unavailable.
          }
        }

        const isGranted = await ensurePushNotificationsEnabled({
          studyId: study.id,
          public_key: publicKey,
        });

        if (!isGranted) {
          setPushPermissionError(true);
          return;
        }

        setPushPermissionError(false);
      }

      await patchNotificationPreferences(study.id, { [channel]: value });
      await getCurrent();
    } catch (error) {
      console.error("Failed to update notification preference", error);
    } finally {
      setNotificationLoading((prev) => ({ ...prev, [channel]: false }));
    }
  };

  const handleDisconnect = async () => {
    if (!study?.slug) return;

    await endSession();
    resetCaches();
    ionRouter.push(`/${study.slug}/welcome`, "root", "replace", {
      unmount: true,
    });
  };

  return (
    <ResonanceLayout isConnected>
      <PageWrap>
        <Card>
          <SectionHeaderButton type="button" onClick={() => toggleSection("account")}>
            <SectionHeaderLeft>
              <IonIcon icon={settingsOutline} />
              <span>{t("mentalplus_account_manage")}</span>
            </SectionHeaderLeft>
            <IonIcon
              icon={expanded.account ? chevronUpOutline : chevronDownOutline}
            />
          </SectionHeaderButton>

          {expanded.account && (
            <SectionBody>
              {/* Identity the researcher collects, shown READ-ONLY: these are
                  answered once at signup (or by an identity question) and have
                  no self-service edit path yet, so they carry no edit icon. */}
              {collectsField("firstname") && (
                <TextOnlyRow type="button" disabled>
                  <ItemText>
                    <ItemTitle>{t("home_inscription_firstname")}</ItemTitle>
                    <ItemValue>
                      {user?.attributes?.firstname || t("profile_not_provided")}
                    </ItemValue>
                  </ItemText>
                </TextOnlyRow>
              )}

              {collectsField("lastname") && (
                <TextOnlyRow type="button" disabled>
                  <ItemText>
                    <ItemTitle>{t("home_inscription_lastname")}</ItemTitle>
                    <ItemValue>
                      {user?.attributes?.lastname || t("profile_not_provided")}
                    </ItemValue>
                  </ItemText>
                </TextOnlyRow>
              )}

              {collectsField("address") && (
                <TextOnlyRow type="button" disabled>
                  <ItemText>
                    <ItemTitle>{t("home_inscription_address")}</ItemTitle>
                    <ItemValue>
                      {user?.attributes?.address || t("profile_not_provided")}
                    </ItemValue>
                  </ItemText>
                </TextOnlyRow>
              )}

              {collectsField("email") && (
                <ItemButton
                  type="button"
                  onClick={() => canChangePassword && setIsChangeEmailModalOpen(true)}
                >
                  <ItemText>
                    <ItemTitle>{t("profile_email")}</ItemTitle>
                    <ItemValue>
                      {displayEmail || t("profile_not_provided")}
                    </ItemValue>
                  </ItemText>
                  {canChangePassword && (
                    <IonIcon
                      icon={hasEmailValue ? createOutline : addCircleOutline}
                    />
                  )}
                </ItemButton>
              )}

              {collectsField("phonenumber") && (
                <ItemButton
                  type="button"
                  onClick={() =>
                    canChangePassword && setIsChangePhoneNumberModalOpen(true)
                  }
                >
                  <ItemText>
                    <ItemTitle>{t("profile_phone")}</ItemTitle>
                    <ItemValue>{displayPhone || t("profile_not_provided")}</ItemValue>
                  </ItemText>
                  {canChangePassword && (
                    <IonIcon
                      icon={hasPhoneValue ? createOutline : addCircleOutline}
                    />
                  )}
                </ItemButton>
              )}

              {collectsField("nickname") && (
                <ItemButton
                  type="button"
                  onClick={() => canChangeNickname && setIsChangeNicknameModalOpen(true)}
                >
                  <ItemText>
                    <ItemTitle>{t("profile_pseudonym")}</ItemTitle>
                    <ItemValue>{nickname || t("profile_not_provided")}</ItemValue>
                  </ItemText>
                  {canChangeNickname && <IonIcon icon={createOutline} />}
                </ItemButton>
              )}

              <ItemButton
                type="button"
                onClick={() => canChangePassword && setIsChangePwdModalOpen(true)}
              >
                <ItemText>
                  <ItemTitle>{t("profile_change_password")}</ItemTitle>
                  <ItemValue>****************</ItemValue>
                </ItemText>
                {canChangePassword && <IonIcon icon={createOutline} />}
              </ItemButton>

              {/* Study record rather than personal identity — the dossier number
                  the researcher assigned. Read-only: a participant never edits it. */}
              {collectsField("external_id") && (
                <TextOnlyRow type="button" disabled>
                  <ItemText>
                    <ItemTitle>{t("home_inscription_external_id")}</ItemTitle>
                    <ItemValue>
                      {user?.attributes?.external_id || t("profile_not_provided")}
                    </ItemValue>
                  </ItemText>
                </TextOnlyRow>
              )}

              <TextOnlyRow
                type="button"
                onClick={() => setIsLeaveStudyModalOpen(true)}
              >
                <ItemText>
                  <ItemTitle>{t("mentalplus_account_deactivate")}</ItemTitle>
                  <ItemValue>****************</ItemValue>
                </ItemText>
              </TextOnlyRow>

              {nirStatus?.exists && !nirStatus.opposed && (
                <TextOnlyRow
                  type="button"
                  onClick={() => setIsNirOppositionModalOpen(true)}
                >
                  <ItemText>
                    <ItemTitle>{t("profile_nir_opposition")}</ItemTitle>
                  </ItemText>
                </TextOnlyRow>
              )}
              {nirStatus?.exists && nirStatus.opposed && (
                <TextOnlyRow type="button" disabled>
                  <ItemText>
                    <ItemTitle>{t("profile_nir_opposition_done")}</ItemTitle>
                  </ItemText>
                </TextOnlyRow>
              )}
            </SectionBody>
          )}

          <Divider />

          <SectionHeaderButton
            type="button"
            onClick={() => toggleSection("notifications")}
          >
            <SectionHeaderLeft>
              <IonIcon icon={notificationsOutline} />
              <span>{t("mentalplus_account_notifications")}</span>
            </SectionHeaderLeft>
            <IonIcon
              icon={
                expanded.notifications ? chevronUpOutline : chevronDownOutline
              }
            />
          </SectionHeaderButton>

          {expanded.notifications && (
            <SectionBody>
              {/* Same collection rule as the account section: a channel the study
                  never collects an address for cannot be a notification channel,
                  and its "add a contact" fallback would invite the participant to
                  fill a field the page above deliberately hides. */}
              {collectsField("email") &&
                (canEnableEmailNotifications ? (
                <NotificationRow>
                  <ItemText>
                    <ItemTitle>{t("mentalplus_account_notification_email")}</ItemTitle>
                    <ItemValue>{displayEmail}</ItemValue>
                  </ItemText>
                  <NotificationControl>
                    <IonIcon icon={mailOutline} />
                    <StyledToggle
                      checked={emailNotificationsEnabled}
                      disabled={notificationLoading.email}
                      onIonChange={(event) =>
                        updateNotificationPreference("email", event.detail.checked)
                      }
                    />
                  </NotificationControl>
                </NotificationRow>
              ) : (
                <ItemButton
                  type="button"
                  onClick={() =>
                    canChangePassword && setIsChangeEmailModalOpen(true)
                  }
                  disabled={!canChangePassword}
                >
                  <ItemText>
                    <ItemTitle>{t("mentalplus_account_notification_email")}</ItemTitle>
                    <ItemValue>
                      {t("mentalplus_account_notification_add_contact")}
                    </ItemValue>
                  </ItemText>
                  {canChangePassword && <IonIcon icon={addCircleOutline} />}
                </ItemButton>
                ))}

              {collectsField("phonenumber") &&
                (canEnableSmsNotifications ? (
                <NotificationRow>
                  <ItemText>
                    <ItemTitle>{t("mentalplus_account_notification_sms")}</ItemTitle>
                    <ItemValue>{displayPhone}</ItemValue>
                  </ItemText>
                  <NotificationControl>
                    <IonIcon icon={chatbubbleEllipsesOutline} />
                    <StyledToggle
                      checked={smsNotificationsEnabled}
                      disabled={notificationLoading.sms}
                      onIonChange={(event) =>
                        updateNotificationPreference("sms", event.detail.checked)
                      }
                    />
                  </NotificationControl>
                </NotificationRow>
              ) : (
                <ItemButton
                  type="button"
                  onClick={() =>
                    canChangePassword && setIsChangePhoneNumberModalOpen(true)
                  }
                  disabled={!canChangePassword}
                >
                  <ItemText>
                    <ItemTitle>{t("mentalplus_account_notification_sms")}</ItemTitle>
                    <ItemValue>
                      {t("mentalplus_account_notification_add_contact")}
                    </ItemValue>
                  </ItemText>
                  {canChangePassword && <IonIcon icon={addCircleOutline} />}
                </ItemButton>
                ))}

              <NotificationRow>
                <ItemText>
                  <ItemTitle>{t("mentalplus_account_notification_push")}</ItemTitle>
                  <ItemValue>
                    {pushPermissionError
                      ? t("mentalplus_account_notification_push_permission_required")
                      : t("mentalplus_account_notification_push_hint")}
                  </ItemValue>
                </ItemText>
                <NotificationControl>
                  <IonIcon icon={notificationsOutline} />
                  <StyledToggle
                    checked={effectivePushNotificationsEnabled}
                    disabled={notificationLoading.push}
                    onIonChange={(event) =>
                      updateNotificationPreference("push", event.detail.checked)
                    }
                  />
                </NotificationControl>
              </NotificationRow>
            </SectionBody>
          )}

          <Divider />

          <DirectRowButton type="button" disabled>
            <SectionHeaderLeft>
              <IonIcon icon={schoolOutline} />
              <span>{t("mentalplus_account_tutorials")}</span>
            </SectionHeaderLeft>
          </DirectRowButton>

          {hasLangChoice && (
            <>
              <Divider />

              <DirectRowButton
                type="button"
                onClick={() => setLangModalVisible(true)}
              >
                <SectionHeaderLeft>
                  <IonIcon icon={languageOutline} />
                  <span>{t("mentalplus_account_language")}</span>
                </SectionHeaderLeft>
              </DirectRowButton>
            </>
          )}

          <Divider />

          <SectionHeaderButton type="button" onClick={() => toggleSection("about")}>
            <SectionHeaderLeft>
              <IonIcon icon={informationCircleOutline} />
              <span>{t("mentalplus_account_about")}</span>
            </SectionHeaderLeft>
            <IonIcon icon={expanded.about ? chevronUpOutline : chevronDownOutline} />
          </SectionHeaderButton>

          {expanded.about && (
            <SectionBody>
              <TextOnlyRow
                type="button"
                onClick={() => setIsLegalMentionOpen(true)}
              >
                <ItemText>
                  <ItemTitle>{t("profile_information_note")}</ItemTitle>
                </ItemText>
              </TextOnlyRow>

              <TextOnlyRow type="button" onClick={() => setIsAboutOpen(true)}>
                <ItemText>
                  <ItemTitle>{t("mentalplus_menu_equipe")}</ItemTitle>
                </ItemText>
              </TextOnlyRow>

              <TextOnlyRow type="button" disabled>
                <ItemText>
                  <ItemTitle>{t("mentalplus_account_app_version")}</ItemTitle>
                  <ItemValue>{`${app?.name} 1.0`}</ItemValue>
                </ItemText>
              </TextOnlyRow>
            </SectionBody>
          )}

          <Divider />

          <DirectRowButton type="button" onClick={() => setIsReportBugOpen(true)}
          >
            <SectionHeaderLeft>
              <IonIcon icon={bugOutline} />
              <span>{t("mentalplus_account_contact")}</span>
            </SectionHeaderLeft>
          </DirectRowButton>
        </Card>

        <LogoutButton type="button" onClick={handleDisconnect}>
          <IonIcon icon={logOutOutline} />
          <span>{t("menu_logOut")}</span>
        </LogoutButton>

        <LeaveStudyModal
          onDismiss={() => setIsLeaveStudyModalOpen(false)}
          isOpen={isLeaveStudyModalOpen}
        />
        <NirOppositionModal
          isOpen={isNirOppositionModalOpen}
          onDismiss={() => setIsNirOppositionModalOpen(false)}
          onOpposed={() => setNirStatus({ exists: true, opposed: true })}
        />
        <ChangePwdModal
          onDismiss={() => setIsChangePwdModalOpen(false)}
          isOpen={isChangePwdModalOpen}
        />
        <ChangeEmailModal
          onDismiss={() => setIsChangeEmailModalOpen(false)}
          isOpen={isChangeEmailModalOpen}
          hasExistingEmail={Boolean(validEmail)}
        />
        <ChangePhoneNumberModal
          onDismiss={() => setIsChangePhoneNumberModalOpen(false)}
          isOpen={isChangePhoneNumberModalOpen}
          hasExistingPhoneNumber={Boolean(validPhone)}
        />
        <ChangeNicknameModal
          onDismiss={() => setIsChangeNicknameModalOpen(false)}
          isOpen={isChangeNicknameModalOpen}
        />
        <LegalMentionModal
          onDismiss={() => setIsLegalMentionOpen(false)}
          isOpen={isLegalMentionOpen}
        />
        {/* Mounted only when there is a choice — the modal fetches the study's
            languages on mount, so keeping it around for a row that can never
            open it would just repeat that call. */}
        {hasLangChoice && (
          <LanguageSelectorModal
            modalVisible={langModalVisible}
            onCloseClick={() => setLangModalVisible(false)}
          />
        )}
        <ReportBugModal
          isOpen={isReportBugOpen}
          onDismiss={() => setIsReportBugOpen(false)}
          user={user}
          study={study}
        />
        <AboutModal
          isOpen={isAboutOpen}
          onDismiss={() => setIsAboutOpen(false)}
        />
      </PageWrap>
    </ResonanceLayout>
  );
};

/**
 * Laptop/desktop step. The page is authored mobile-first, and every phone-tuned
 * value here needs a denser counterpart above this width: the type scale (the
 * vw clamps top out on wide screens, so desktop was getting the LARGEST text)
 * and the vertical rhythm (a 0.8rem row rhythm that reads generous on a 390px
 * phone stacks up on a laptop, where an open section used to push the logout
 * button off a 648px viewport).
 */
const DESKTOP = "@media (min-width: 768px)";

const PageWrap = styled.div`
  width: 100%;
  max-width: 760px;
  margin: 0 auto;
  padding: 1.1rem 1rem 1rem;

  ${DESKTOP} {
    /* A settings list doesn't need 760px; the narrower column reads as one
       object instead of labels marooned from their controls. */
    max-width: 680px;
    padding: 0.7rem 1rem 1rem;
  }
`;

const Card = styled.section`
  background: var(--app-color-surface-tint, #f5f7f9);
  border-radius: 28px;
  overflow: hidden;
  border: 1px solid rgba(var(--app-color-text-rgb, 4, 23, 35), 0.12);
`;

const SectionHeaderButton = styled.button`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1rem 1.2rem;
  border: 0;
  background: transparent;
  color: var(--app-color-text, #0c2742);
  font-size: 1rem;
  font-weight: 600;

  &:disabled {
    cursor: default;
    opacity: 1;
  }

  ${DESKTOP} {
    padding: 0.65rem 1.1rem;
  }
`;

const DirectRowButton = styled(SectionHeaderButton)`
  justify-content: flex-start;
`;

const SectionHeaderLeft = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.75rem;

  ion-icon {
    color: rgba(var(--app-color-text-rgb, 4, 23, 35), 0.55);
    font-size: 1.9rem;
  }

  span {
    font-size: clamp(1rem, 2.7vw, 1.2rem);
  }

  /* The vw term above only overtakes the 1rem floor around 590px, so its growth
     never fires on a phone — only on tablet/desktop, where a phone-tuned scale
     already reads oversized. Pin the type back to the mobile base and bring the
     glyphs down to a desktop weight. */
  ${DESKTOP} {
    ion-icon {
      font-size: 1.5rem;
    }

    span {
      font-size: 1.05rem;
    }
  }
`;

const SectionBody = styled.div`
  padding: 0 1.2rem 0.6rem;

  ${DESKTOP} {
    padding: 0 1.1rem 0.25rem;
  }
`;

const LogoutButton = styled.button`
  width: 100%;
  min-height: 58px;
  margin: 1rem 0 0;
  border: none;
  border-radius: 30px;
  /* Action button -> secondary family (see tokens.ts roles). */
  background: var(--app-color-secondary, #ffce48);
  color: var(--app-color-secondary-contrast, #1b1b1b);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.7rem;
  padding: 0 20px;
  font-size: 18px;
  font-weight: 700;
  cursor: pointer;
  box-shadow: 0px 5px 5px rgba(0, 0, 0, 0.1);

  ion-icon {
    font-size: 1.4rem;
  }

  &:active {
    transform: translateY(1px);
  }

  ${DESKTOP} {
    min-height: 48px;
    margin-top: 0.7rem;
    font-size: 1rem;

    ion-icon {
      font-size: 1.2rem;
    }
  }
`;

const ItemButton = styled.button`
  width: 100%;
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 0.8rem;
  border: 0;
  background: transparent;
  padding: 0.8rem 0;
  color: var(--app-color-text, #0c2742);

  ion-icon {
    flex-shrink: 0;
    color: rgba(var(--app-color-text-rgb, 4, 23, 35), 0.55);
    font-size: 1.8rem;
    margin-top: 0.2rem;
  }

  ${DESKTOP} {
    padding: 0.4rem 0;

    ion-icon {
      font-size: 1.4rem;
    }
  }
`;

const NotificationRow = styled.div`
  width: 100%;
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 0.8rem;
  padding: 0.8rem 0;
  color: var(--app-color-text, #0c2742);

  /* Same rhythm as ItemButton — these sit side by side in the same section. */
  ${DESKTOP} {
    padding: 0.4rem 0;
  }
`;

const NotificationControl = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.6rem;

  ion-icon {
    flex-shrink: 0;
    color: rgba(var(--app-color-text-rgb, 4, 23, 35), 0.55);
    font-size: 1.35rem;
  }

  ${DESKTOP} {
    ion-icon {
      font-size: 1.2rem;
    }
  }
`;

const StyledToggle = styled(IonToggle)`
  --handle-width: 18px;
  --handle-height: 18px;
  --track-background: rgba(var(--app-color-text-rgb, 4, 23, 35), 0.25);
  /* Toggle = interactive control -> accent family (see tokens.ts roles). */
  --track-background-checked: var(--app-color-accent, #2d9f75);
`;

const TextOnlyRow = styled(ItemButton)`
  ion-icon {
    display: none;
  }

  &:disabled {
    opacity: 1;
    cursor: default;
  }
`;

const ItemText = styled.span`
  display: inline-flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 0.2rem;
  min-width: 0;

  ${DESKTOP} {
    gap: 0.1rem;
  }
`;

const ItemTitle = styled.span`
  font-size: clamp(1rem, 2.9vw, 1.1rem);
  font-weight: 600;
  color: var(--app-color-text, #0c2742);
  text-align: left;

  ${DESKTOP} {
    font-size: 1rem;
  }
`;

const ItemValue = styled.span`
  font-size: clamp(0.95rem, 2.7vw, 1.05rem);
  color: rgba(var(--app-color-text-rgb, 4, 23, 35), 0.55);
  text-align: left;
  word-break: break-word;

  ${DESKTOP} {
    font-size: 0.95rem;
  }
`;

const Divider = styled.hr`
  border: 0;
  border-top: 1px solid rgba(var(--app-color-text-rgb, 4, 23, 35), 0.12);
  margin: 0;
`;

export default DisplayAccount;